import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import MainCard from "../../components/layout/MainCard/MainCard";
import useArray from "../../hooks/useArray";
import Sitemap from "../../routes/sitemap";
import CatalogueAside from "./Aside/CatalogueAside";
import Products from "./Products/Products";
import useAPI from "hooks/useAPI";
import Breadcrumbs from "../../components/layout/BreadCrumbs/Breadcrumbs";
import scss from "./catalogue.module.scss";
import { useTranslation } from "react-i18next";
import useDefaultProductSort, { DEFAULT_SORT_FALLBACK } from "hooks/useDefaultProductSort";

/**
 * The main page that shows multiple products.
 */
const CataloguePage = () => {
    const { t } = useTranslation();
    const api = useAPI();
    const { searchTerm } = useParams();
    const { pathname } = useLocation();
    const trimmed = pathname.trim("/");
    const parts = trimmed.split("/");
    const category = parts[parts.length - 1];
    const [limit, setLimit] = useState(12);
    const id = useParams();
    const splittedId = id["*"]?.split("/");
    const categoryid = splittedId[splittedId?.length - 1];
    const { data, isLoading, ...products } = useQuery(["CataloguePageSingleCategory", categoryid], async () => await api.get(`/categories/product/single/${categoryid}`));

    const navigate = useNavigate();
    const location = useLocation();
    // The available and active filters
    const [activeFilters, setActiveFilters] = useArray([]);
    // The category we are searching for
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [changeFilters, setChangeFilters] = useState(false);
    let title = data?.basic_data?.name;
    let categoryId = activeCategory?.id ?? 0;
    let search = "";
    const {
        data: filters,
        isFetched,
        isSuccess,
        ...basic
    } = useQuery(
        ["CataloguePage.Filters", categoryid],
        async () => {
            return await api
                .post(`/products/category/filters/${categoryid}`, {
                    filters: selectedFilters,
                })
                .then((res) => {
                    setAvailableFilters(res);
                    return res;
                });
        },
        {
            refetchOnWindowFocus: false,
        }
    );

    const [availableFilters, setAvailableFilters] = useState();
    const [lastSelectedFilterKey, setLastSelectedFilterKey] = useState("");

    useEffect(() => {
        if (changeFilters) {
            basic
                .refetch({
                    filters: selectedFilters ?? [],
                })
                .then((response) => {
                    const lastSelectedFilterValues = selectedFilters?.find((item) => {
                        return item?.column === lastSelectedFilterKey;
                    });
                    const lastSelectedFilter = availableFilters?.find((item) => {
                        return item?.key === lastSelectedFilterKey;
                    });

                    const filterLastSelectedFromResponse = response?.data?.filter((item) => {
                        return item?.key !== lastSelectedFilterKey;
                    });

                    const indexOfLastSelectedFilter = availableFilters?.findIndex((index) => {
                        return index?.key === lastSelectedFilterKey;
                    });

                    if (lastSelectedFilter && lastSelectedFilterValues?.value?.selected?.length > 0) {
                        setAvailableFilters([
                            ...filterLastSelectedFromResponse.slice(0, indexOfLastSelectedFilter),
                            lastSelectedFilter,
                            ...filterLastSelectedFromResponse.slice(indexOfLastSelectedFilter),
                        ]);
                    } else {
                        setAvailableFilters(response?.data);
                    }
                });
            setChangeFilters(false);
        }
    }, [changeFilters]);

    // Navigate to category
    const navigateToCategory = (category) => {
        navigate(Sitemap.catalogue._ + (category?.path ?? ""));
        setActiveCategory(category);
    };

    // The used search term
    if (searchTerm) {
        title = `${t("catalogue_category-products-text-search")}: "${searchTerm}"`;
        categoryId = 0;
        search = searchTerm;
    }
    const params = useParams();

    let crumbs = [];
    let path = data?.breadcrumbs?.start?.slug;
    crumbs[0] = { name: `${t("global-default-text-home")}`, url: "/pocetna" };
    path += "/katalog/kategorija";
    for (const item of data?.breadcrumbs?.steps ?? []) {
        path += "/" + item.slug;
        crumbs.push({ name: item.name, url: path });
    }

    crumbs.push({ name: data?.breadcrumbs?.end?.name, url: "#" });

    //push selected filters to URL
    const { resolveSort, isLoading: isDefaultSortLoading } = useDefaultProductSort();
    const [sort, setSort] = useState(DEFAULT_SORT_FALLBACK);
    // True once `sort` holds a real value (from the URL or the admin default) rather than the
    // placeholder fallback — gates URL-writing so it never persists the placeholder into the URL.
    const [sortResolved, setSortResolved] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const [newQuery, setNewQuery] = useState();
    const url = new URL(window.location.href);
    // Skips the very first run after `sort` gets resolved, so the initial resolution (from the
    // URL, localStorage, or the CMS default) is never echoed back into the URL — only a genuine,
    // later user-driven change (dropdown, pagination, filters) is. Without this, the initial write
    // gets read back by the restore effect below as if it were "real" URL state, corrupting it.
    const skipNextUrlWrite = useRef(true);
    useEffect(() => {
        // Don't write the placeholder fallback sort into the URL — wait until the real
        // admin-configured default (or the URL-restore effect below) has set the real sort.
        if (!sortResolved) return;

        if (skipNextUrlWrite.current) {
            skipNextUrlWrite.current = false;
            return;
        }

        const updateQuery = () => {
            const filterQuery = selectedFilters
                .map((item) => {
                    if (item?.column && item?.value?.selected?.length > 0) {
                        const selectedValues = item.value.selected.map((value) => encodeURIComponent(value)).join("___");
                        return `${encodeURIComponent(item.column)}=${selectedValues}`;
                    }
                    return null;
                })
                .filter(Boolean)
                .join("&");

            const primarySort = sort[sort.length - 1];
            const sortQuery = primarySort?.field && primarySort?.direction ? `${selectedFilters?.length === 0 ? "" : "&"}sort=${primarySort.field}___${primarySort.direction}` : "";
            const pageQuery = currentPage ? `&page=${currentPage}` : "";
            const limitQuery = limit ? `&limit=${limit}` : "";
            setNewQuery(filterQuery || sortQuery || pageQuery || limitQuery ? `?${filterQuery}${sortQuery}${pageQuery}${limitQuery}` : "");
        };

        updateQuery();
    }, [selectedFilters, sort, currentPage, limit, sortResolved]);
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        // Wait for the admin-configured default sort to load before resolving the initial
        // sort state, so we don't briefly apply the hardcoded fallback then overwrite it.
        if (isDefaultSortLoading) return;

        const extractFiltersFromQuery = async () => {
            const filtersURL = url?.search?.slice(1, url?.search?.length);
            const splittedFiltersURL = filtersURL?.split("&");
            //filter out page= and sort= from filtersURL
            const splittedFiltersURL2 = splittedFiltersURL?.filter((item) => {
                return !item.includes("page=") && !item.includes("sort=") && !item.includes("limit=");
            });

            const sortQuery = searchParams?.get("sort");
            const page = searchParams?.get("page");
            const limitURLQuery = searchParams?.get("limit");
            const sortArray = sortQuery?.split("___") ?? [];
            const sortObject = {
                field: sortArray[0] ?? "",
                direction: sortArray[1] ?? "",
            };

            if (!filtersURL) {
                setSort(resolveSort());
                setSortResolved(true);
                return;
            }

            const filterObject = splittedFiltersURL2?.map((filter) => {
                const [column, value] = (filter ?? "").split("=");
                const selected = (value ?? "").split("___").map((val) => {
                    return column === "p-r|cena" ? Number(decodeURI(val)) : decodeURIComponent(val);
                });

                return {
                    column: decodeURIComponent(column),
                    value: {
                        selected: selected,
                    },
                };
            });

            setSelectedFilters(filterObject);
            setSort(resolveSort(sortQuery));
            setSortResolved(true);
            setCurrentPage(page);
            setLimit(limitURLQuery ?? 8);

            await products.refetch({
                filters: filterObject,
                sort: sortObject,
                page: page,
                limit: limitURLQuery,
            });
            setTimeout(async () => {
                await basic.refetch({
                    filters: filterObject,
                });
                setChangeFilters(true);
            }, 1000);

            //ukljuciti kada bude imala paginacija
            // if (page) {
            //   setPage(page);
            // }
        };
        const showButton = document.getElementById("showButton");
        if (showButton) {
            setTimeout(() => {
                showButton.click();
            }, 1000);
        }
        extractFiltersFromQuery();
    }, [isDefaultSortLoading]);
    useEffect(() => {
        if (newQuery) {
            const path = `/katalog/kategorija/${categoryid}${newQuery}`;

            window.history.replaceState(null, "", path);
        }
    }, [newQuery]);
    const REACT_APP_URL = localStorage.getItem("api");

    return (
        <MainCard>
            {/* Shown only when printing */}
            <div className={scss.printHeader}>
                <img src={`${REACT_APP_URL ? REACT_APP_URL : process.env.REACT_APP_URL}configuration/images/presentation/basic/logo`} alt="logo" />
            </div>

            {/* The list of available categories */}

            <CatalogueAside
                // activeCategory={activeCategory}
                // setActiveCategory={setActiveCategory}
                // onActivateCategory={navigateToCategory}
                setLastSelectedFilterKey={setLastSelectedFilterKey}
                lastSelectedFilterKey={lastSelectedFilterKey}
                availableFilters={availableFilters}
                setActiveFilters={setActiveFilters}
                isFetched={isFetched}
                setSelectedFilters={setSelectedFilters}
                selectedFilters={selectedFilters}
                changeFilters={changeFilters}
                setChangeFilters={setChangeFilters}
                setNewQuery={setNewQuery}
                newQuery={newQuery}
                categoryid={categoryid}
                setCurrentPage={setCurrentPage}
                translateKey={"catalogue_category-catalogue_aside"}
            />
            {!isLoading && (
                <div className={scss.breadcrumbs}>
                    <Breadcrumbs crumbs={crumbs} />
                </div>
            )}

            {sortResolved && (
            <Products
                showFilters={true}
                title={title}
                url={`/products/category/list/${categoryid}`}
                banners={false}
                search={search}
                location={location?.pathname}
                sort={sort}
                setSort={setSort}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                setLimit={setLimit}
                limit={limit}
                setLastSelectedFilterKey={setLastSelectedFilterKey}
                lastSelectedFilterKey={lastSelectedFilterKey}
                availableFilters={availableFilters}
                setActiveFilters={setActiveFilters}
                isFetched={isFetched}
                setSelectedFilters={setSelectedFilters}
                selectedFilters={selectedFilters}
                changeFilters={changeFilters}
                categoryid={categoryid}
                setChangeFilters={setChangeFilters}
                setNewQuery={setNewQuery}
                newQuery={newQuery}
                translateKey={"catalogue_category-products"}
            />
            )}
        </MainCard>
    );
};

export default CataloguePage;
